/**
 * Cart stock limit (Vintage Supply Theme)
 *
 * Most pieces have very low stock (often a single unit), so a variant can only
 * be added up to the quantity actually in stock. When an add would push the
 * cart past available inventory, it is blocked and a popup explains why — no
 * redirect to the cart page.
 *
 * Inventory source: the add-to-cart form carries data-variant-inventory (set in
 * sections/main-product.liquid and kept in sync by product-info.js). When that
 * attribute is missing (e.g. a quick-add elsewhere), the limit falls back to 1,
 * matching the "one of a kind" default.
 *
 * Cart page: any line above its allowed quantity is reset and the popup shown.
 */

(function () {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cartUrl = root + 'cart';
  var busy = false;

  function isAddForm(form) {
    try {
      var action = form.getAttribute('action') || form.action || '';
      return /\/cart\/add(\.js)?$/.test(new URL(action, location.href).pathname);
    } catch (error) {
      return false;
    }
  }

  function submitForm(form) {
    HTMLFormElement.prototype.submit.call(form);
  }

  // Reads the inventory cap the form advertises; defaults to 1 when unknown.
  function formLimit(form) {
    var el = form.querySelector('[data-variant-inventory]');
    var raw = el ? el.getAttribute('data-variant-inventory') : null;
    var n = parseInt(raw, 10);
    if (isNaN(n) || n < 0) return 1;
    return n;
  }

  function cartQtyForVariant(cart, variantId) {
    var qty = 0;
    cart.items.forEach(function (item) {
      if (String(item.variant_id) === String(variantId) || String(item.id) === String(variantId)) {
        qty += Number(item.quantity) || 0;
      }
    });
    return qty;
  }

  /* ---------- Popup ---------- */

  function injectStyles() {
    if (document.getElementById('cart-limit-styles')) return;
    var css =
      '.cart-limit-popup{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;' +
      'justify-content:center;padding:1rem;opacity:0;transition:opacity 180ms ease;}' +
      '.cart-limit-popup[data-open]{opacity:1;}' +
      '.cart-limit-popup__overlay{position:absolute;inset:0;background:rgba(0,0,0,.45);}' +
      '.cart-limit-popup__dialog{position:relative;max-width:26rem;width:100%;background:var(--color-background,#fff);' +
      'color:var(--color-foreground,#1a1a1a);border-radius:var(--style-border-radius-inputs,6px);' +
      'padding:1.75rem;box-shadow:0 20px 50px rgba(0,0,0,.25);transform:translateY(8px);' +
      'transition:transform 180ms ease;text-align:center;}' +
      '.cart-limit-popup[data-open] .cart-limit-popup__dialog{transform:translateY(0);}' +
      '.cart-limit-popup__title{margin:0 0 .5rem;font-family:var(--font-heading--family,serif);font-size:1.25rem;}' +
      '.cart-limit-popup__text{margin:0 0 1.5rem;font-size:var(--font-body--size,1rem);line-height:1.5;' +
      'color:color-mix(in oklab,var(--color-foreground,#1a1a1a) 80%,transparent);}' +
      '.cart-limit-popup__actions{display:flex;gap:.75rem;justify-content:center;flex-wrap:wrap;}' +
      '.cart-limit-popup__btn{appearance:none;cursor:pointer;border-radius:var(--style-border-radius-inputs,6px);' +
      'padding:.7rem 1.4rem;font:inherit;font-size:.95rem;border:1px solid currentColor;}' +
      '.cart-limit-popup__btn--primary{background:var(--color-button-background,#2f3826);' +
      'color:var(--color-button-text,#fff);border-color:transparent;}' +
      '.cart-limit-popup__btn--secondary{background:transparent;color:inherit;}' +
      '.cart-limit-popup__close{position:absolute;top:.5rem;right:.5rem;width:2.25rem;height:2.25rem;' +
      'border:0;background:transparent;color:inherit;font-size:1.5rem;line-height:1;cursor:pointer;}';
    var style = document.createElement('style');
    style.id = 'cart-limit-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // Builds the shopper-facing limit message for a given stock/in-cart state.
  function buildLimitMessage(limit, inCart) {
    var allowed = limit - inCart;
    if (allowed <= 0) {
      if (limit <= 1) {
        return { title: 'One of a kind', text: 'This piece is one of a kind and is already in your cart.' };
      }
      return { title: 'Limited availability', text: 'You already have all ' + limit + ' available in your cart.' };
    }
    if (limit <= 1) {
      return { title: 'One of a kind', text: 'Only one of this piece is available.' };
    }
    return {
      title: 'Limited availability',
      text:
        'Only ' + limit + ' available' +
        (inCart > 0 ? ', and you already have ' + inCart + ' in your cart' : '') +
        '. You can add up to ' + allowed + '.',
    };
  }

  function showLimitPopup(opts) {
    var message = buildLimitMessage(opts.limit, opts.inCart);

    // Prefer the bottom-right cart notification toast (same UI as a normal add).
    var toast = document.querySelector('cart-notification');
    if (toast && typeof toast.renderError === 'function' && typeof toast.show === 'function') {
      toast.renderError(message.text);
      toast.show();
      return;
    }

    // Fallback: a small modal, only used when the toast is unavailable.
    injectStyles();
    var title = message.title;
    var text = message.text;

    var existing = document.querySelector('.cart-limit-popup');
    if (existing) existing.remove();

    var popup = document.createElement('div');
    popup.className = 'cart-limit-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.innerHTML =
      '<div class="cart-limit-popup__overlay" data-close></div>' +
      '<div class="cart-limit-popup__dialog">' +
      '<button type="button" class="cart-limit-popup__close" data-close aria-label="Close">&times;</button>' +
      '<h2 class="cart-limit-popup__title">' + title + '</h2>' +
      '<p class="cart-limit-popup__text">' + text + '</p>' +
      '<div class="cart-limit-popup__actions">' +
      '<a class="cart-limit-popup__btn cart-limit-popup__btn--primary" href="' + cartUrl + '">View cart</a>' +
      '<button type="button" class="cart-limit-popup__btn cart-limit-popup__btn--secondary" data-close>Keep browsing</button>' +
      '</div></div>';

    document.body.appendChild(popup);
    // Force a frame so the opening transition runs.
    requestAnimationFrame(function () {
      popup.setAttribute('data-open', '');
    });

    function close() {
      popup.removeAttribute('data-open');
      setTimeout(function () {
        popup.remove();
      }, 200);
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
    }
    popup.addEventListener('click', function (e) {
      if (e.target.closest('[data-close]')) close();
    });
    document.addEventListener('keydown', onKey);
  }

  /* ---------- Intercept Add to Cart Forms ---------- */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement) || !isAddForm(form)) return;
      // Ajax forms (PDP buy buttons) are gated inside cart-notification.js so the
      // inventory check happens before the add and the cart count never bumps on
      // a blocked add. We only handle plain (non-ajax) add forms here.
      if (form.hasAttribute('data-ajax-cart')) return;

      var data = new FormData(form);
      var variantId = data.get('id');
      if (!variantId) return;

      event.preventDefault();
      if (busy) return;
      busy = true;

      var requested = Number(data.get('quantity')) || 1;
      var limit = formLimit(form);

      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          var inCart = cartQtyForVariant(cart, variantId);

          if (inCart + requested > limit) {
            busy = false;
            showLimitPopup({ limit: limit, inCart: inCart });
          } else {
            submitForm(form);
          }
        })
        .catch(function () {
          submitForm(form);
        });
    },
    true
  );

  // Restore state on back button navigation
  window.addEventListener('pageshow', function () {
    busy = false;
  });

  /* ---------- Enforce stock limit on the Cart page ---------- */
  function enforceCartLimits() {
    var items = document.querySelector('[data-cart-items]');
    if (!items) return;

    var rows = Array.prototype.slice.call(items.querySelectorAll('[data-variant-id]'));
    var extra = rows.filter(function (row) {
      var max = Number(row.dataset.max) || 1;
      return Number(row.dataset.quantity) > max;
    });

    if (extra.length) {
      extra
        .reduce(function (chain, row) {
          return chain.then(function () {
            var max = Number(row.dataset.max) || 1;
            return fetch(root + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: row.dataset.key || row.dataset.variantId, quantity: max }),
            });
          });
        }, Promise.resolve())
        .then(function () {
          location.replace(cartUrl + '?limit=' + encodeURIComponent(extra[0].dataset.variantId));
        })
        .catch(function () {});
      return;
    }

    var params = new URLSearchParams(location.search);
    var limited = params.get('limit');
    if (!limited) return;

    rows.forEach(function (row) {
      if (String(row.dataset.variantId) !== String(limited)) return;
      var message = row.querySelector('[data-limit-message]');
      if (message) message.hidden = false;
    });

    params.delete('limit');
    var query = params.toString();
    history.replaceState(null, '', location.pathname + (query ? '?' + query : '') + location.hash);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforceCartLimits);
  } else {
    enforceCartLimits();
  }

  // Shared with cart-notification.js so ajax adds reuse the same gate + popup.
  window.CartLimit = {
    showLimitPopup: showLimitPopup,
    buildLimitMessage: buildLimitMessage,
    formLimit: formLimit,
    cartQtyForVariant: cartQtyForVariant,
    cartUrl: cartUrl,
    root: root,
  };
})();
